import { useContext, useMemo } from 'react';
import { ChatResponseType, type Chat } from '@trinity/chat';
import { ChatSessionQueriesContext } from '../Chat/context';
import { isRecord } from './DynamicForm.form.utils';

/**
 * Whether the chat holds a newer card for the same draft that the agent made, which makes this card out of date: what the person did in
 * it would be refused. Only a card with an `agentNote` counts, because the extraction flow makes several cards for a draft (a draft
 * saved, a form submitted), and those do not replace the one before. A card with no draft yet is never superseded.
 */
export function isSupersededByAgentCard(
  queries: Record<string, Chat.Query> | undefined,
  queryIdentifier: string | undefined,
  draftId: string | undefined
): boolean {
  const own = queryIdentifier ? queries?.[queryIdentifier] : undefined;
  if (!queries || !own || !draftId) return false;

  return Object.values(queries).some(
    (query) =>
      query.identifier !== own.identifier &&
      query.createdOn.getTime() > own.createdOn.getTime() &&
      query.response?.type === ChatResponseType.FormWorkflow &&
      isRecord(query.state) &&
      query.state['draftId'] === draftId &&
      typeof query.state['agentNote'] === 'string'
  );
}

export function useIsSupersededByAgentCard(queryIdentifier: string | undefined, draftId: string | undefined): boolean {
  const queries = useContext(ChatSessionQueriesContext);
  return useMemo(() => isSupersededByAgentCard(queries, queryIdentifier, draftId), [queries, queryIdentifier, draftId]);
}
